import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { listMySummons, respondToSummon } from "@/lib/api/parent-portal.functions";
import { LoadErrorState } from "@/components/parent/LoadErrorState";
import { describeLoadError } from "@/lib/browser-storage";

export const Route = createFileRoute("/_authenticated/responsavel/convocacoes")({
  head: () => ({
    meta: [
      { title: "Convocações | FutBux" },
      { name: "description", content: "Convocações e status de pagamento dos seus alunos." },
      { property: "og:title", content: "Convocações | FutBux" },
      { property: "og:description", content: "Convocações e status de pagamento dos seus alunos." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const fmtBRL = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function Page() {
  const listFn = useServerFn(listMySummons);
  const respondFn = useServerFn(respondToSummon);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [studentId, setStudentId] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    const res = await listFn();
    setRows(res.summons ?? []);
  };
  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void reload(); }, []);

  const students = useMemo(() => {
    const map = new Map<string, string>();
    rows.forEach((r) => r.student?.id && map.set(r.student.id, r.student.name ?? "?"));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (studentId !== "all" && r.student?.id !== studentId) return false;
    if (status !== "all" && (r.status ?? "pending") !== status) return false;
    return true;
  }), [rows, studentId, status]);

  const counters = useMemo(() => {
    const c = { total: rows.length, confirmed: 0, declined: 0, pending: 0, paid: 0 };
    rows.forEach((r) => {
      const s = r.status ?? "pending";
      if (s === "confirmed") c.confirmed++;
      else if (s === "declined") c.declined++;
      else c.pending++;
      if (r.fee?.status === "pago" || r.fee?.status === "conferido") c.paid++;
    });
    return c;
  }, [rows]);

  const respond = async (id: string, s: "confirmed" | "declined") => {
    setSavingId(id);
    try {
      await respondFn({ data: { summonStudentId: id, status: s } });
      await load();
      toast.success(s === "confirmed" ? "Presença confirmada" : "Ausência registrada");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao responder");
    } finally { setSavingId(null); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;

  if (error) {
    return (
      <div className="p-4 max-w-3xl mx-auto">
        <LoadErrorState
          rawError={error}
          message={describeLoadError(error, "Erro ao carregar as convocações.")}
          onRetry={() => void reload()}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Convocações</h1>
        <p className="text-muted-foreground text-sm">Jogos em que seus alunos foram convocados.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { l: "Total", v: counters.total, cls: "text-foreground" },
          { l: "Confirmados", v: counters.confirmed, cls: "text-emerald-600" },
          { l: "Pendentes", v: counters.pending, cls: "text-amber-600" },
          { l: "Recusados", v: counters.declined, cls: "text-red-600" },
        ].map((s) => (
          <Card key={s.l}><CardContent className="p-3">
            <div className="text-xs text-muted-foreground">{s.l}</div>
            <div className={`text-xl font-bold ${s.cls}`}>{s.v}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="border rounded-md h-9 px-2 text-sm bg-background"
          value={studentId} onChange={(e) => setStudentId(e.target.value)}>
          <option value="all">Todos alunos</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className="border rounded-md h-9 px-2 text-sm bg-background"
          value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos status</option>
          <option value="pending">Pendente</option>
          <option value="confirmed">Confirmado</option>
          <option value="declined">Recusado</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="p-6 text-sm text-muted-foreground">Nenhuma convocação encontrada.</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((r) => {
            const g = r.game ?? {};
            const paid = r.fee?.status === "pago" || r.fee?.status === "conferido";
            const st = r.status ?? "pending";
            return (
              <Card key={r.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2"><CalendarClock className="h-4 w-4" /> {r.student?.name}</span>
                    <Badge variant={st === "confirmed" ? "default" : st === "declined" ? "destructive" : "secondary"}>
                      {st === "confirmed" ? "Confirmado" : st === "declined" ? "Recusado" : "Pendente"}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><b>{g.opponent ?? "-"}</b> {g.event_categories?.category ? `• ${g.event_categories.category}` : ""}</div>
                  <div className="text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {g.match_date ? new Date(`${g.match_date}T${g.match_time ?? "00:00"}`).toLocaleString("pt-BR") : "-"}
                  </div>
                  {g.location && <div className="text-muted-foreground">📍 {g.location}</div>}
                  {r.fee && (
                    <div className="flex items-center justify-between rounded-md border p-2">
                      <span>Taxa: <b>{fmtBRL(Number(r.fee.amount ?? 0))}</b></span>
                      <Badge variant={paid ? "default" : "secondary"}>{paid ? "Pago" : "Aguardando"}</Badge>
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant={st === "confirmed" ? "default" : "outline"}
                      disabled={savingId === r.id}
                      onClick={() => respond(r.id, "confirmed")}>
                      <Check className="h-3 w-3 mr-1" /> Confirmar
                    </Button>
                    <Button size="sm" variant={st === "declined" ? "destructive" : "outline"}
                      disabled={savingId === r.id}
                      onClick={() => respond(r.id, "declined")}>
                      <X className="h-3 w-3 mr-1" /> Recusar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
